<i18n lang="json">
{
  "fr": {
    "add-group": "Groupe",
    "add-prop": "Condition",
    "group-and": "Et",
    "group-or": "Ou",
    "condition-type": "Type de condition",
    "delete-group": "Supprimer le groupe"
  },
  "en": {
    "add-group": "Group",
    "add-prop": "Condition",
    "group-and": "And",
    "group-or": "Or",
    "condition-type": "Condition type",
    "delete-group": "Delete group"
  }
}
</i18n>
<template>
  <div
    :data-sub-groups-depth="subGroupsDepth"
    class="relative rounded border border-t-2 border-primary-main py-2 pl-6 pr-4"
    :class="[{ 'group-one': props.groupLevel === 1 }, { 'group-two': props.groupLevel > 1 }]">
    <span
      v-if="props.groupLevel > 0"
      class="absolute left-[calc((1rem+1px)*-1)] top-1/2 h-px w-[calc(1rem+1px)] bg-grey-700"></span>
    <span
      v-if="props.groupLevel > 0"
      class="absolute bottom-1/2 left-[calc((1rem+1px)*-1)] h-[calc(50%+1.5rem)] w-px bg-grey-700"
      :class="
        props.groupLevel === 1
          ? 'group-one-last:bottom-1/2 group-one-last:h-[calc(50%+1.5rem)]'
          : 'group-two-last:bottom-1/2 group-two-last:h-[calc(50%+1.5rem)]'
      "></span>

    <VueDraggable
      handle=".handle"
      :group="draggableGroupConfig"
      ghost-class="opacity-50"
      animation="150"
      :model-value="children"
      item-key="_id"
      @change="handleDraggableChange">
      <template #header>
        <!-- Header du groupe -->
        <div class="mb-6 flex items-center gap-2">
          <PfIcon v-if="showHandle" class="handle cursor-move opacity-60" size="sm" :icon="ICON_DRAG_HANDLE" />
          <div class="grow xs:flex xs:flex-row-reverse xs:justify-between xs:gap-2">
            <!-- Boutons d'ajout -->
            <div class="mb-4 ml-auto mr-0 flex flex-wrap justify-end gap-2 xs:mb-0">
              <PfButtonAction
                :btn-style="props.groupLevel > 0 ? 'outline' : 'primary'"
                :icon="ICON_PLUS"
                size="sm"
                has-icon-left
                :label="t('add-prop')"
                @click="addPropertyAt(children.length)" />
              <PfButtonAction
                v-if="props.maxDepth > 0"
                :btn-style="props.groupLevel > 0 ? 'outline' : 'primary'"
                :icon="ICON_PLUS"
                size="sm"
                has-icon-left
                :label="t('add-group')"
                @click="addGroupAt(children.length)" />
              <PfButtonAction
                v-if="props.groupLevel > 0"
                btn-style="outline"
                size="sm"
                is-icon-only
                :icon="ICON_TRASH"
                :aria-labelledby="t('delete-group')"
                @click="() => emit('delete')" />
            </div>

            <!-- ET / OU -->
            <UiSwitch :model-value="orConditionEnabled" :label="t('condition-type')" @update:model-value="toggleGroupType">
              <template #left>
                <span class="mr-2 text-xs font-bold uppercase">{{ t("group-and") }}</span>
              </template>
              <template #right>
                <span class="ml-2 text-xs font-bold uppercase">{{ t("group-or") }}</span>
              </template>
            </UiSwitch>
          </div>
        </div>
      </template>
      <template #item="{ element: child, index }">
        <div>
          <!-- Éditeur (sous-groupe ou propriété) -->
          <ConditionGroup
            v-if="'group' in child"
            class="ml-4 mt-5"
            show-handle
            :group-level="props.groupLevel + 1"
            :properties="props.properties"
            :model-value="child"
            :max-depth="props.maxDepth - 1"
            @update:model-value="(x: GroupCondition) => updateAt(index, x)"
            @delete="() => removeAt(index)" />

          <ConditionProperty
            v-else
            ref="lastProp"
            show-handle
            :properties="props.properties"
            :model-value="child"
            @update:model-value="(x) => updateAt(index, x)"
            @delete="() => removeAt(index)" />
        </div>
      </template>
      <template #footer> </template>
    </VueDraggable>
    <div
      v-if="props.groupLevel === 0 || children.length <= 0"
      class="relative ml-4 mt-3 flex flex-wrap items-center justify-center gap-2 rounded-md border-2 border-dashed border-primary-tint py-1 pl-4 pr-1 last:mb-3">
      <PfButtonAction
        btn-style="outline"
        :icon="ICON_PLUS"
        size="sm"
        has-icon-left
        :label="t('add-prop')"
        @click="addPropertyAt(children.length)" />
      <PfButtonAction
        v-if="props.maxDepth > 0"
        btn-style="outline"
        :icon="ICON_PLUS"
        size="sm"
        has-icon-left
        :label="t('add-group')"
        @click="addGroupAt(children.length)" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Condition, ConditionEditorProperty, GroupCondition } from "@/lib/helpers/condition";
import { computed, watchEffect, ref, nextTick } from "vue";
import { useI18n } from "vue-i18n";

import VueDraggable from "vuedraggable";

import ConditionProperty from "./property.vue";
import ConditionGroup from "./group.vue";

import ICON_PLUS from "@/lib/icons/plus.json";
import ICON_TRASH from "@/lib/icons/trash.json";
import ICON_DRAG_HANDLE from "@/lib/icons/burger.json";

const { t } = useI18n();

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: "update:modelValue", condition: GroupCondition): void;
  // eslint-disable-next-line no-unused-vars
  (e: "delete"): void;
}>();

const props = withDefaults(
  defineProps<{
    groupLevel?: number;
    modelValue: GroupCondition;
    properties?: ConditionEditorProperty[];
    maxDepth: number;
    showHandle?: boolean;
  }>(),
  {
    // default values
    groupLevel: 0,
    properties: undefined
  }
);

const orConditionEnabled = computed(() => (props.modelValue.group === "OR" ? true : false));

const children = computed(() => props.modelValue?.children ?? []);

/* Générer un ID unique temporaire pour chaque étément du groupe.
 * Ça sert de key pour le template. Ces ID sont perdus lors de la
 * sauvegarde puisque le backend désérialise le JSON reçu. */
watchEffect(() => addIds(children.value));
function addIds(children: (Condition & { _id?: string })[]) {
  for (const child of children) {
    if (!child._id) {
      child._id = Date.now().toString() + Math.random().toString();
    }
  }
}

/* Calcul de la profondeur des sous groupes.
 * Si le groupe actuel contient un sous-groupe qui contient à son tour un sous-groupe,
 * la profondeur est de 2
 */
const subGroupsDepth = computed(() => {
  function depth(group: GroupCondition): number {
    if (!group.children) return 0;
    var subGroups = group.children.filter((g): g is GroupCondition => "group" in g);
    if (subGroups.length === 0) return 0;

    return Math.max(...subGroups.map(depth)) + 1;
  }

  return depth(props.modelValue);
});

const draggableGroupConfig = {
  name: "condition-group",
  put(to: unknown, from: unknown, element: HTMLElement) {
    // Ne pas accepter un groupe si notre profondeur maximale n'est pas suffisante
    // pour la profondeur du groupe qui s'en vient
    var groupElement: HTMLElement | null = element.querySelector("[data-sub-groups-depth]");
    if (!!groupElement && props.maxDepth <= Number(groupElement.dataset.subGroupsDepth)) {
      return false;
    }
    return true;
  }
};

type DraggableChangedEvent<T> = {
  added?: { newIndex: number; element: T };
  removed?: { oldIndex: number; element: T };
  moved?: { newIndex: number; oldIndex: number; element: T };
};
function handleDraggableChange({ added, removed, moved }: DraggableChangedEvent<Condition>) {
  if (moved) {
    const newChildren = [...props.modelValue.children];
    newChildren.splice(moved.oldIndex, 1);
    newChildren.splice(moved.newIndex, 0, moved.element);
    onUpdate({ children: newChildren });
  }

  if (added) {
    const newChildren = [...props.modelValue.children];
    newChildren.splice(added.newIndex, 0, added.element);
    onUpdate({ children: newChildren });
  }

  if (removed) {
    nextTick(() => {
      const newChildren = [...props.modelValue.children];
      newChildren.splice(removed.oldIndex, 1);
      onUpdate({ children: newChildren });
    });
  }
}

function onUpdate(newValues: Partial<GroupCondition>) {
  emit("update:modelValue", { ...props.modelValue, ...newValues });
}

function updateAt(index: number, value: Condition) {
  const newChildren = [...children.value];
  newChildren.splice(index, 1, value);

  onUpdate({ children: newChildren });
}

function addGroupAt(index: number) {
  const newChildren = [...children.value];
  newChildren.splice(index, 0, { group: "AND", children: [] });

  onUpdate({ children: newChildren });
}

/* C'est un peu un hack:
 * En temps normal, comme le composant qui a cette ref est dans une boucle,
 * on s'attendrait à avoir une array dans la ref.
 * Par contre, c'est vuedraggable qui gère la liste, et il ne crée pas d'array.
 * C'est donc toujours le dernier élément qui est dans la ref.
 * C'est toutefois parfait pour notre besoin d'ouvrir le popup d'édition après
 * l'ajout d'une propriété, puisque la nouvelle propriété est toujours à la fin
 * de la liste.
 * */
const lastProp = ref<{ openModal?: () => void }>();

function addPropertyAt(index: number) {
  const newChildren = [...children.value];
  newChildren.splice(index, 0, { prop: null });

  onUpdate({ children: newChildren });

  nextTick(() => {
    const editor = lastProp.value;
    if (editor?.openModal) {
      editor.openModal();
    }
  });
}

function removeAt(index: number) {
  const newChildren = [...children.value];
  newChildren.splice(index, 1);

  onUpdate({ children: newChildren });
}

function toggleGroupType() {
  const newCondition = props.modelValue.group === "OR" ? "AND" : "OR";
  onUpdate({ group: newCondition });
}
</script>
